<!--
  Компонент для просмотра уже прикрепленных файлов.

  Используется в карточках, модалках и read-only секциях, где нужно показать
  список attachment из API без загрузки новых файлов. Для редактирования и
  добавления файлов используйте AttachmentUploader.
-->
<template>
  <div class="attachments-viewer" :class="{ 'attachments-viewer--compact': compact }">
    <span v-if="!compact" class="viewer-title">Прикрепленные файлы</span>
    <div
      class="attachments-container"
      :class="{ 'attachments-container--compact': compact }"
    >
      <div
        v-for="(attachment, index) in attachments"
        :key="attachment.id || index"
        class="attachment-item"
        :class="{ 'attachment-item--compact': compact }"
      >
        <div
          class="attachment-preview-container"
          @click.stop="openAttachment(attachment)"
        >
          <img
            v-if="isImage(attachment)"
            :src="attachmentPreviewUrl(attachment)"
            class="attachment-preview"
            @error="handlePreviewError($event, attachment)"
          />
          <div v-else class="file-icon-container">
            <div
              class="file-icon"
              :class="getFileIconClass(attachment)"
            >
              <span class="file-extension">
                {{ getFileExtension(attachment) }}
              </span>
            </div>
            <div class="file-type">
              {{ getFileTypeName(attachment) }}
            </div>
          </div>
        </div>

        <div v-if="!compact" class="attachment-info">
          <div class="attachment-name" :title="attachmentName(attachment)">
            {{ truncateFileName(attachmentName(attachment)) }}
          </div>
          <div class="attachment-size">
            {{ formatFileSize(attachmentSize(attachment)) }}
          </div>
        </div>
      </div>
    </div>

    <BaseAttachmentPreview
      v-if="previewItem"
      :attachment="previewItem"
      @close="previewItem = null"
    />
  </div>
</template>

<script>
import {
  isImage,
  getFileIconClass,
  getFileExtension,
  getFileTypeName,
  truncateFileName,
  formatFileSize,
  attachmentFileUrl,
  attachmentName,
  attachmentPreviewUrl,
  attachmentSize,
} from "@/utils/fileUtils";

export default {
  name: "AttachmentViewer",
  props: {
    attachments: {
      type: Array,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      previewItem: null,
    };
  },
  methods: {
    openAttachment(attachment) {
      this.previewItem = attachment;
    },
    handlePreviewError(event, attachment) {
      const fallbackUrl = attachmentFileUrl(attachment);

      if (!fallbackUrl || event.target.src === fallbackUrl) return;

      event.target.src = fallbackUrl;
    },
    isImage,
    getFileIconClass,
    getFileExtension,
    getFileTypeName,
    truncateFileName,
    formatFileSize,
    attachmentName,
    attachmentPreviewUrl,
    attachmentSize,
  },
};
</script>

<style scoped>
.attachments-viewer {
  margin-top: 20px;
}

.viewer-title {
  font-size: 14px;
}

.attachments-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 10px;
}
.attachments-container--compact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.attachment-item {
  position: relative;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
}
.attachment-item--compact {
  width: 68px;
  min-height: 68px;
  height: 68px;
  padding: 6px;
  flex-direction: row;
  justify-content: center;
}
.attachment-item:hover {
  border-color: var(--color-green);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.attachment-preview-container {
  position: relative;
  width: 100%;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
}
.attachment-item--compact .attachment-preview-container {
  width: 54px;
  height: 54px;
  flex-basis: 54px;
  margin-right: 0;
  margin-bottom: 0;
}
.attachment-preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}
.file-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
.file-icon {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 5px;
}
.attachment-item--compact .file-icon {
  width: 42px;
  height: 42px;
}
.file-icon.image-icon {
  background-color: #e3f2fd;
  color: #1976d2;
}
.file-icon.pdf-icon {
  background-color: #ffebee;
  color: #f44336;
}
.file-icon.word-icon {
  background-color: #e8f5e9;
  color: #4caf50;
}
.file-icon.excel-icon {
  background-color: #e8f5e9;
  color: #2e7d32;
}
.file-icon.archive-icon {
  background-color: #f3e5f5;
  color: #9c27b0;
}
.file-icon.text-icon {
  background-color: #e0f7fa;
  color: #00bcd4;
}
.file-icon.default-icon {
  background-color: #f5f5f5;
  color: #616161;
}
.file-extension {
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  background-color: white;
  padding: 2px 6px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}
.file-type {
  font-size: 11px;
  color: #666;
  text-align: center;
  margin-top: 10px;
}
.attachment-item--compact .file-type {
  display: none;
}
.attachment-info {
  width: 100%;
  text-align: center;
}
.attachment-name {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 3px;
  font-weight: 500;
}
.attachment-size {
  font-size: 10px;
  color: #666;
}

</style>
